import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Tours et Pylônes Télécoms — IS TELECOM',
  description: 'Conception, fabrication et installation de tours de télécommunication : pylônes autostables, haubanés, monopôles et mâts tubulaires. Normes ANSI/TIA-222, partenaires Huawei, Ericsson, MTN.',
  provider: {
    '@type': 'Organization',
    name: 'IS TELECOM',
    url: 'https://is-telecom.co',
  },
  areaServed: 'Afrique, Asie, Europe, Amériques',
};

export default function TelecomInfrastructures() {
  const { t } = useTranslation();

  const services = [
    {
      title: 'Pylônes Autostables',
      desc: 'Conception et installation de pylônes autostables jusqu\'à 120 mètres de hauteur. Structures en treillis d\'acier galvanisé, résistantes aux vents cycloniques et aux conditions climatiques extrêmes d\'Afrique.',
      icon: 'ri-signal-tower-line',
      image: 'https://readdy.ai/api/search-image?query=Tall%20steel%20lattice%20telecommunications%20tower%20against%20clear%20African%20sky%2C%20self-supporting%20telecom%20structure%20with%20multiple%20antennas%20and%20microwave%20dishes%2C%20professional%20industrial%20infrastructure%20photography%2C%20warm%20natural%20lighting%2C%20clean%20detailed%20scene%20with%20blue%20sky%20background&width=800&height=500&seq=tel-tower-standalone-01&orientation=landscape',
    },
    {
      title: 'Tours Haubanées',
      desc: 'Déploiement de tours haubanées optimisées pour les zones à fort vent. Structure légère, fondation réduite, coût compétitif. Idéal pour les zones rurales et les sites d\'émission radio.',
      icon: 'ri-anchor-line',
      image: 'https://readdy.ai/api/search-image?query=Guyed%20telecommunications%20tower%20with%20cable%20stays%20in%20open%20African%20landscape%2C%20tall%20slender%20steel%20mast%20with%20anchor%20cables%2C%20rural%20telecom%20infrastructure%20against%20expansive%20sky%2C%20professional%20engineering%20photography%20with%20natural%20warm%20tones&width=800&height=500&seq=tel-tower-guyed-01&orientation=landscape',
    },
    {
      title: 'Monopôles Urbains',
      desc: 'Monopôles design et compacts pour les zones urbaines denses. Faible encombrement au sol, esthétique soignée, intégration harmonieuse dans l\'environnement urbain. Hauteur jusqu\'à 45 mètres.',
      icon: 'ri-building-4-line',
      image: 'https://readdy.ai/api/search-image?query=Sleek%20modern%20telecom%20monopole%20tower%20in%20African%20cityscape%2C%20single%20steel%20pole%20with%20camouflaged%20antennas%2C%20urban%20telecommunications%20infrastructure%20with%20clean%20design%2C%20professional%20architectural%20photography%20with%20warm%20natural%20lighting%20and%20blue%20sky&width=800&height=500&seq=tel-monopole-01&orientation=landscape',
    },
    {
      title: 'Génie Civil & Fondations',
      desc: 'Études de sol, fondations profondes, dalles de béton armé, réseaux de mise à la terre et blindage électromagnétique. Génie civil conforme aux normes Eurocodes et locales.',
      icon: 'ri-building-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20BTP%20civil%20engineering%20construction%20workers%20with%20dark%20skin%20building%20reinforced%20concrete%20foundation%20for%20telecommunications%20tower%20in%20Africa%2C%20professional%20dark-skinned%20workers%20pouring%20concrete%20and%20installing%20steel%20rebar%20cage%2C%20safety%20helmets%20and%20vests%2C%20bright%20daylight%20industrial%20photography&width=800&height=500&seq=tel-civil-01&orientation=landscape&nocache=true',
    },
  ];

  const stats = [
    { value: '1000', suffix: '+', label: t('about.statProjects') || 'Sites déployés' },
    { value: '120', suffix: 'm', label: 'Hauteur max pylône' },
    { value: '50', suffix: '+', label: 'Pays couverts' },
    { value: '4', suffix: 'h', label: 'Intervention SLA' },
  ];

  return (
    <Layout>
      <JsonLd data={schema} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-background-950">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://readdy.ai/api/search-image?query=Dramatic%20aerial%20view%20of%20multiple%20telecommunications%20towers%20across%20African%20landscape%20at%20golden%20hour%2C%20cellular%20network%20infrastructure%20with%20antennas%20and%20microwave%20links%2C%20cinematic%20wide%20shot%20with%20warm%20sunset%20lighting%2C%20professional%20telecom%20photography%20with%20dark%20moody%20atmosphere&width=1920&height=900&seq=tel-infra-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-accent-400/50 rounded-full px-5 py-2.5 mb-8">
            <i className="ri-radio-line text-accent-400 text-sm w-4 h-4 flex items-center justify-center" />
            <span className="text-accent-300 text-sm font-medium uppercase tracking-wider">Télécommunications</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-white mb-6 leading-[1.1]">
            Infrastructures<br />
            <span className="text-accent-400">Télécoms</span>
          </h1>
          <p className="text-lg md:text-xl text-background-200 max-w-2xl mx-auto leading-relaxed mb-10">
            Conception, fabrication et installation de tours et pylônes de télécommunication.
            Partenaire de Huawei, Ericsson, MTN et Orange depuis 2006.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold font-heading text-accent-400 mb-1">
                  {s.value}<span className="text-2xl">{s.suffix}</span>
                </div>
                <div className="text-background-300 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <ScrollReveal variant="fade-up">
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Nos Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                De la conception à l&apos;installation
              </h2>
              <p className="text-foreground-500 mt-4 max-w-2xl mx-auto">
                Nous maîtrisons l&apos;ensemble de la chaîne : étude d&apos;ingénierie, génie civil, fabrication, transport, montage et mise en service.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <div key={svc.title} className="group bg-white rounded-3xl overflow-hidden border border-background-200/70 hover:shadow-xl transition-all duration-500">
                  <div className="h-56 overflow-hidden">
                    <img src={svc.image} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <i className={`${svc.icon} text-primary-600 text-xl w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <h3 className="text-xl font-bold font-heading text-foreground-950">{svc.title}</h3>
                    </div>
                    <p className="text-foreground-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Normes & Certifications */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                  <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Normes & Sécurité</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
                  Conformité et<br />sécurité garanties
                </h2>
                <p className="text-foreground-600 text-lg leading-relaxed mb-8">
                  Toutes nos structures sont conçues et installées selon les normes internationales les plus strictes.
                  Nos équipes sont formées aux protocoles de sécurité en hauteur et aux standards de l&apos;industrie.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', title: 'ANSI/TIA-222', desc: 'Norme américaine pour structures de support d\'antennes' },
                    { icon: 'ri-earth-line', title: 'Eurocode 3', desc: 'Conception des structures en acier' },
                    { icon: 'ri-flashlight-line', title: 'Normes CEM', desc: 'Compatibilité électromagnétique et mise à la terre' },
                    { icon: 'ri-award-line', title: 'Certifié Huawei & Ericsson', desc: 'Formation continue aux équipements des partenaires' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-primary-600 text-lg w-5 h-5 flex items-center justify-center`} />
                      </div>
                      <div>
                        <div className="font-semibold text-foreground-950 text-sm">{item.title}</div>
                        <div className="text-foreground-500 text-xs">{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="https://storage.helloreaddy.io/project_files/dd4db55e-adff-421a-ab07-f57f9542cdee/10260502-bb37-441d-8546-90487bad8ae7_compressed_ChatGPT-Image-5-aot-2026-18_30_41.webp"
                  alt="Sécurité sur les tours de télécommunication"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">
            Prêt à déployer votre infrastructure ?
          </h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            De l&apos;étude de faisabilité à la mise en service, nous accompagnons chaque étape de votre projet de télécommunication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-primary-400 text-primary-400 hover:bg-primary-500/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}